import { useCallback, useEffect, useRef, useState } from 'react';

// S5 ("stay, then move", mockup variant A) — placeholder durations. The
// mockup's own README flags these as unsettled ("3s is a placeholder");
// picked here to be long enough to read + tap Undo without feeling stuck.
export const HOLD_MS = 3000;
export const GLOW_MS = 2000;
export const TOAST_MS = 4000;
// Reject/cancel toasts linger a beat longer — they carry a typed reason the
// technician may not have been looking for, and (for cancel) they're the
// ONLY record left once the card is gone from the board.
export const TOAST_LONG_MS = 6000;

/**
 * useLabQueueMoveFeedback — the local UI state behind S5's "stay, then move"
 * feedback layer. No store, no endpoint: `run()` is always one of the
 * existing lifecycle actions (collectSpecimen/start/uploadReportsAndComplete/
 * rejectSample), just called LATER (after the hold) instead of immediately.
 *
 * - `startHold(id, meta)` — hold `id` in place; `meta.run` fires when the
 *   hold's `holdMs` (default 3s) elapses, UNLESS undone or flushed first.
 *   Exactly one hold per id: starting a new one flushes any old one for the
 *   same id rather than queuing a second (the caller decides whether that's
 *   reachable — LabQueueView keeps the triggering button disabled instead).
 * - `undoHold(id)` — cancels a still-pending hold; `run()` is NEVER called,
 *   so nothing was sent and nothing needs reversing.
 * - `flush(id)` / `flushAll()` — fire a hold's `run()` immediately (used on
 *   unmount, tab-hide, or a competing action on the same card) — a tap is
 *   never silently lost.
 * - `glow` / `toast` / `announcement` — the "just landed" highlight, the
 *   bottom toast, and the aria-live text, all driven by the same `fire()`.
 */
export default function useLabQueueMoveFeedback() {
  const [holds, setHolds] = useState({});
  const [glow, setGlow] = useState(null); // { id, tag }
  const [toast, setToast] = useState(null); // { id, text, targetId?, laneKey? }
  const [announcement, setAnnouncement] = useState('');

  // `holdsRef` is the SOURCE OF TRUTH the guards below read. `holds` (state)
  // only exists to trigger a re-render for the stripe UI — it must never be
  // the thing a re-entrant call checks, because a `setHolds` update doesn't
  // land in `holds` until the next render, and a synchronous re-entrant call
  // (e.g. onReject's flush() -> startHold() for the next competing action,
  // all in ONE event, no render in between) would still see the stale
  // snapshot. Every mutation below writes `holdsRef.current` FIRST, synchronously, then
  // mirrors it into state for rendering.
  const holdsRef = useRef(holds);
  const timersRef = useRef({});
  const cardRefs = useRef({});
  const mountedRef = useRef(true);

  const clearTimer = (key) => {
    const t = timersRef.current[key];
    if (t) clearTimeout(t);
    delete timersRef.current[key];
  };

  const dropHold = (id) => {
    if (!(id in holdsRef.current)) return;
    const n = { ...holdsRef.current };
    delete n[id];
    holdsRef.current = n;
    setHolds(n);
  };

  // The ONE path a hold's action reaches the network — its own expiry and an
  // early flush both call this, so it can never fire twice for one hold.
  // `dropHold` (which marks the hold as fired, synchronously, in `holdsRef`)
  // runs BEFORE `hold.run()` is even called, so any re-entrant call that
  // happens during `run()`'s async gap (or a `startHold` for the same id
  // right after `flush` returns) sees the hold already gone.
  const fire = useCallback(async (id) => {
    const hold = holdsRef.current[id];
    if (!hold) return;
    clearTimer(id);
    dropHold(id);
    const r = await hold.run();
    if (!mountedRef.current) return;
    if (r && r.success === false) {
      // The deferred call failed (e.g. a duplicate sample number) — the
      // stripe is already gone, so this is the only place left to surface
      // it. No glow, no lane-move toast; the card stays where it was.
      const key = `err_${id}_${Date.now()}`;
      setToast({ id: key, text: r.error || 'Could not complete that action.' });
      setAnnouncement(r.error || 'Action failed.');
      clearTimer(key);
      timersRef.current[key] = setTimeout(
        () => setToast((cur) => (cur && cur.id === key ? null : cur)),
        TOAST_LONG_MS
      );
      return;
    }
    if (hold.glowTag) {
      setGlow({ id, tag: hold.glowTag });
      clearTimer(`glow_${id}`);
      timersRef.current[`glow_${id}`] = setTimeout(
        () => setGlow((cur) => (cur && cur.id === id ? null : cur)),
        GLOW_MS
      );
    }
    if (hold.laneTitle) {
      const label = hold.patientLabel || 'Patient';
      const moveKey = `move_${id}_${Date.now()}`;
      setAnnouncement(`${label} moved to ${hold.laneTitle}`);
      // S6 — `laneKey` (additive, optional) lets the caller's "Jump →" switch
      // the status-card filter to the card's DESTINATION lane before
      // scrolling, so a move into a currently-hidden lane is still reachable.
      setToast({ id: moveKey, text: `${label} · moved to ${hold.laneTitle}`, targetId: id, laneKey: hold.laneKey || null });
      clearTimer(`toast_${id}`);
      timersRef.current[`toast_${id}`] = setTimeout(
        () => setToast((cur) => (cur && cur.id === moveKey ? null : cur)),
        hold.toastMs || TOAST_MS
      );
    }
  }, []);

  const startHold = useCallback((id, meta) => {
    // One pending action per card — flush whatever was already held for
    // this id before installing the new one (never queued, never dropped).
    // `fire` drops it from `holdsRef` synchronously, so by the time it
    // returns the slot below is genuinely empty, not a stale snapshot.
    if (holdsRef.current[id]) fire(id);
    const n = { ...holdsRef.current, [id]: meta };
    holdsRef.current = n;
    setHolds(n);
    clearTimer(id);
    timersRef.current[id] = setTimeout(() => fire(id), meta.holdMs ?? HOLD_MS);
  }, [fire]);

  const undoHold = useCallback((id) => {
    clearTimer(id);
    dropHold(id);
  }, []);

  const flush = useCallback((id) => {
    if (holdsRef.current[id]) fire(id);
  }, [fire]);

  const flushAll = useCallback(() => {
    Object.keys(holdsRef.current).forEach((id) => fire(id));
  }, [fire]);

  // Never lose a pending tap: fire everything still held if the tab is
  // hidden or the view unmounts.
  useEffect(() => {
    const onVisibility = () => { if (document.hidden) flushAll(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      flushAll();
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A standalone toast with no hold behind it (cancel — the card leaves the
  // board, so there's no lane to glow and nothing to jump to).
  const notify = useCallback((text, { announce, durationMs = TOAST_LONG_MS } = {}) => {
    const key = `note_${Date.now()}_${Math.random()}`;
    setAnnouncement(announce || text);
    setToast({ id: key, text });
    timersRef.current[key] = setTimeout(
      () => setToast((cur) => (cur && cur.id === key ? null : cur)),
      durationMs
    );
  }, []);

  const registerCardRef = useCallback((id, el) => {
    if (el) cardRefs.current[id] = el;
    else delete cardRefs.current[id];
  }, []);

  const scrollToCard = useCallback((id) => {
    const el = cardRefs.current[id];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  return {
    holds, glow, toast, announcement,
    startHold, undoHold, flush, flushAll, notify,
    registerCardRef, scrollToCard,
  };
}
